<script setup>
/*==================================================================*/
/*================== VUE IMPORTS ===================================*/
/*==================================================================*/

import { ref, computed, onMounted, provide } from 'vue';

/*==================================================================*/
/*================== IMPORTS DOS COMPONENTES =======================*/
/*==================================================================*/

import TelaTabela from '@/components/v2/telas/TelaTabela/TelaTabela.vue';
import BotaoTextoBase from '@/components/v2/base/BotaoTextoBase/BotaoTextoBase.vue';
import BotaoIconeBase from '@/components/v2/base/BotaoIconeBase/BotaoIconeBase.vue';
import CriarDadoModal from './CriarDado/CriarDadoModal.vue';
import VisualisarDadoModal from './VisualisarDado/VisualizarDadoModal.vue';
import EditarDadoModal from './EditarDado/EditarDadoModal.vue';
import AprovarDadoModal from './AprovarDado/AprovarDadoModal.vue';

/*==================================================================*/
/*=================== IMPORTS DO USUARIO ===========================*/
/*==================================================================*/

import useUsuarioStore from '@/api/v2/stores/UsuarioStore.js';
const usuarioStore = useUsuarioStore();

/*==================================================================*/
/*================= IMPORTS DO COLABORADOR =========================*/
/*==================================================================*/

import useColaboradorStore from '@/api/v2/stores/ColaboradorStore.js';
const colaboradorStore = useColaboradorStore();

/*==================================================================*/
/*================== IMPORTS DE SERVICES ===========================*/
/*==================================================================*/

import OrdemDePagamentoService from '@/api/v2/services/OrdemDePagamentoService.js';
import UtilArray from '@/utils/UtilArray.mjs';

/*==================================================================*/
/*================== MODAL CRIAR DADO REF ==========================*/
/*==================================================================*/

const modalCriarDadoRef = ref();

/*==================================================================*/
/*================== MODAL VISUALIZAR DADO REF =====================*/
/*==================================================================*/

const modalVisualizarDadoRef = ref();

/*==================================================================*/
/*================== MODAL EDITAR DADO REF =========================*/
/*==================================================================*/

const modalEditarDadoRef = ref();

/*==================================================================*/
/*================== MODAL APROVAR DADO REF ========================*/
/*==================================================================*/

const modalAprovarDadoRef = ref();

/*==================================================================*/
/*================== CONTROLES DESATIVADOS =========================
/*==================================================================*/

const disable = ref(false);

/*==================================================================*/
/*================== REFS DE DADOS E LOADED ========================*/
/*==================================================================*/

const ordensDePagamento = ref([]);
const ordemDePagamentoRequisitantes = ref([]);
const ordemDePagamentoCredorColaboradores = ref([]);
const ordemDePagamentoCredorFornecedores = ref([]);
const ordemDePagamentoDespesas = ref([]);
const ordemDePagamentoJobs = ref([]);

const loadedOrdemDePagamentoRequisitantes = ref(false);
const loadedOrdemDePagamentoCredorColaboradores = ref(false);
const loadedOrdemDePagamentoCredorFornecedores = ref(false);
const loadedOrdemDePagamentoDespesas = ref(false);
const loadedOrdemDePagamentoJobs = ref(false);

/*==================================================================*/
/*================== DADOS DE ORDEM DE PAGAMENTO ===================*/
/*==================================================================*/

const dados = ref([]);

/*==================================================================*/
/*================== FILTROS DE ORDEM DE PAGAMENTO =================*/
/*==================================================================*/

const dadoNumeroFiltro = ref('');
const dadoTipoFiltro = ref('');
const dadoCredorFiltro = ref('');

/*==================================================================*/
/*================== HEADERS DA TABELA =============================*/
/*==================================================================*/

const dadosHeaders = [
    { text: 'N°',                        value: 'numero',                   type: 'string'  },
    { text: 'Status',                    value: 'status',                   type: 'string'  },
    { text: 'Data de Criação',           value: 'dataCriacao',              type: 'date'    },
    { text: 'Emitente',                  value: 'emitente.nome',            type: 'string'  },
    { text: 'Requisitante',              value: 'requisitante.nome',        type: 'string'  },
    { text: 'Tipo',                      value: 'tipo',                     type: 'string'  },
    { text: 'Credor',                    value: 'credor',                   type: 'string'  },
    { text: 'Forma de Pagamento',        value: 'formaDePagamento',         type: 'string'  },
    { text: 'Valor Total',               value: 'valorTotal',               type: 'money'   },
    { text: 'Data Previsão Pagamento',   value: 'dataPrevisaoPagamento',    type: 'date'    },
    { text: 'Data Aprovação Pagamento',  value: 'dataAprovacaoPagamento',   type: 'date'    },
];

/*==================================================================*/
/*================== ALTERA CONTROLES DESATIVADOS ==================*/
/*==================================================================*/

const alteraDisable = (update = false) => {

    disable.value = update;

};

/*==================================================================*/
/*================== VALIDAÇÕES DE FILTRO ==========================*/
/*==================================================================*/

const dadoNumeroFiltroValido = (numero) => {

    if(dadoNumeroFiltro.value === ''){
        return true;
    }

    if(numero != dadoNumeroFiltro.value){
        return false;
    }

    return true;

};

const dadoTipoFiltroValido = (tipo) => {

    if(dadoTipoFiltro.value === ''){
        return true;
    }

    if(tipo != dadoTipoFiltro.value){
        return false;
    }

    return true;

};

const dadoCredorFiltroValido = (credor) => {

    if(dadoCredorFiltro.value === ''){
        return true;
    }

    if(credor != dadoCredorFiltro.value){
        return false;
    }

    return true;

};

/*==================================================================*/
/*================== DADOS FILTRADOS ===============================*/
/*==================================================================*/

const dadosFiltrados = computed(() => {

    return dados?.value?.reduce((acc, dado) => {

        if(!dadoNumeroFiltroValido(dado?.numero)){
            return acc;
        }

        if(!dadoTipoFiltroValido(dado?.tipo)){
            return acc;
        }

        if(!dadoCredorFiltroValido(dado?.tipoCredor)){
            return acc;
        }

        acc.push(dado);

        return acc;

    }, []);

});

/*==================================================================*/
/*================== ALTERA FILTROS ================================*/
/*==================================================================*/

const alteraDadoNumeroFiltro = (dado) => {

    dadoNumeroFiltro.value = dado?.numero || '';

};

const alteraDadoTipoFiltro = (dado) => {

    dadoTipoFiltro.value = dado?.tipo || '';

};

const alteraDadoCredorFiltro = (dado) => {

    dadoCredorFiltro.value = dado?.tipoCredor || '';

};

/*==================================================================*/
/*================== FILTROS CONFIGURADOS ==========================*/
/*==================================================================*/

const filtros = computed(() => [

    { label: 'Nº',             items: dados.value, itemText: 'numero',      onChange: alteraDadoNumeroFiltro },
    { label: 'Tipo',           items: dados.value, itemText: 'tipo',        onChange: alteraDadoTipoFiltro },
    { label: 'Tipo de Credor', items: dados.value, itemText: 'tipoCredor',  onChange: alteraDadoCredorFiltro },

]);

/*==================================================================*/
/*================== MÉTODOS DE LOAD DAS VIEWS =====================*/
/*==================================================================*/

const loadOrdensDePagamento = async () => {

    alteraDisable(true);

    const response = await OrdemDePagamentoService.pegaViewOrdemDePagamento();

    ordensDePagamento.value = response?.data || [];

    alteraDisable(false);

};

const loadOrdemDePagamentoRequisitantes = async () => {

    if(loadedOrdemDePagamentoRequisitantes.value){
        return;
    }

    const response = await OrdemDePagamentoService.pegaViewOrdemDePagamentoRequisitantes();
    ordemDePagamentoRequisitantes.value = response?.data;

    loadedOrdemDePagamentoRequisitantes.value = true;

};


const loadOrdemDePagamentoCredorColaboradores = async () => {

    if(loadedOrdemDePagamentoCredorColaboradores.value){
        return;
    }

    const response = await OrdemDePagamentoService.pegaViewOrdemDePagamentoCredorColaboradores();
    ordemDePagamentoCredorColaboradores.value = response?.data;

    loadedOrdemDePagamentoCredorColaboradores.value = true;

};


const loadOrdemDePagamentoCredorFornecedores = async () => {

    if(loadedOrdemDePagamentoCredorFornecedores.value){
        return;
    }

    const response = await OrdemDePagamentoService.pegaViewOrdemDePagamentoCredorFornecedores();
    ordemDePagamentoCredorFornecedores.value = response?.data;

    loadedOrdemDePagamentoCredorFornecedores.value = true;

};


const loadOrdemDePagamentoDespesas = async () => {

    if(loadedOrdemDePagamentoDespesas.value){
        return;
    }

    const response = await OrdemDePagamentoService.pegaViewOrdemDePagamentoDespesas();
    ordemDePagamentoDespesas.value = response?.data;

    loadedOrdemDePagamentoDespesas.value = true;

};


const loadOrdemDePagamentoJobs = async () => {

    if(loadedOrdemDePagamentoJobs.value){
        return;
    }

    const response = await OrdemDePagamentoService.pegaViewOrdemDePagamentoJobs();
    ordemDePagamentoJobs.value = response?.data;

    loadedOrdemDePagamentoJobs.value = true;

};

/*==================================================================*/
/*================== RELOAD ORDENS DE PAGAMENTO ====================*/
/*==================================================================*/

const reloadOrdensDePagamento = async () => {

    await loadOrdensDePagamento();
    preparaDados(ordensDePagamento.value);

};

/*==================================================================*/
/*================== MOSTRA BOTAO EDITAR ===========================*/
/*==================================================================*/

const mostraBotaoEditar = (dado = {}) => {

    return dado?.acessoDeEdicao === 'Sim' ? true : false;

};

/*==================================================================*/
/*================== MOSTRA BOTAO APROVAR ==========================*/
/*==================================================================*/

const mostraBotaoAprovar = (dado = {}) => {

    return dado?.acessoDeAprovacao === 'Sim' ? true : false;

};

/*==================================================================*/
/*=========================== AO CRIAR DADO ========================*/
/*==================================================================*/

const aoCriarDado = async () => {
    
    await loadOrdemDePagamentoRequisitantes();
    await loadOrdemDePagamentoCredorColaboradores();
    await loadOrdemDePagamentoCredorFornecedores();
    await loadOrdemDePagamentoDespesas();
    await loadOrdemDePagamentoJobs();
    
    await modalCriarDadoRef.value?.abrirModal();
};

/*==================================================================*/
/*================== AO VISUALIZAR DADO ============================*/
/*==================================================================*/

const aoVisualizarDado = async (dado) => {

    await modalVisualizarDadoRef.value?.abrirModal(dado);

};

/*==================================================================*/
/*==================== AO EDITAR DADO ==============================*/
/*==================================================================*/

const aoEditarDado = async (dado) => {

    await loadOrdemDePagamentoRequisitantes();
    await loadOrdemDePagamentoCredorColaboradores();
    await loadOrdemDePagamentoCredorFornecedores();
    await loadOrdemDePagamentoDespesas();
    await loadOrdemDePagamentoJobs();

    await modalEditarDadoRef.value?.abrirModal(dado);

};

/*==================================================================*/
/*=================== AO APROVAR DADO ==============================*/
/*==================================================================*/

const aoAprovarDado = async (dado) => {
    await modalAprovarDadoRef.value?.abrirModal(dado);

};

/*==================================================================*/
/*=================== PREPARA ACESSO DE EDICAO =====================*/
/*==================================================================*/

const preparaAcessoDeEdicao = (dado = {}) => {

    //==================================================================//
    // Se a Ordem de Pagamento não tiver o Status de Elaboração (ACESSO DE EDICAO: NÃO)
    //==================================================================//
    const dadoStatus = dado.status ?? '';

    if(dadoStatus !== "Elaboração"){
        return "Não";
    }

    //==================================================================//
    // Se o Usuário for Administrador (ACESSO DE EDICAO: SIM)
    // IGNORA Emitente diferente de Colaborador.
    //==================================================================//
    const usuarioGrupo = usuarioStore?.usuario?.grupoNome ?? '';
    
    if(usuarioGrupo == "Administradores"){
        return "Sim";
    }

    //==================================================================//
    // Se o Colaborador for diferente do Emitente (ACESSO DE EDICAO: NÃO)
    //==================================================================//
    const emitenteID = dado?.emitente?.id ?? '';
    const colaboradorID = colaboradorStore?.colaborador?.id ?? '';

    if(emitenteID !== colaboradorID){
        return "Não";
    }

    //==================================================================//
    // Em qualquer outro caso (ACESSO DE EDICAO: SIM)
    //==================================================================//

    return "Sim";

};

/*==================================================================*/
/*================= PREPARA ACESSO DE APROVACAO ====================*/
/*==================================================================*/

const preparaAcessoDeAprovacao = (dado = {}) => {

    //==================================================================//
    // Se a Ordem de Pagamento não tiver um Status de Aprovação. (ACESSO DE APROVACAO: NÃO)
    //==================================================================//
    const dadoStatus = dado.status ?? '';

    const statusPermitidos = [
        'Aguardando Requisitante',
        'Aguardando Coordenador',
        'Aguardando Gestor',
        'Aguardando Financeiro',
    ];

    if(UtilArray.notIncludes(statusPermitidos, dadoStatus)){
        return "Não";
    }

    //==================================================================//
    // Se o Usuário for Administrador (ACESSO DE APROVACAO: SIM)
    // IGNORA Validação de Aprovador
    //==================================================================//
    const usuarioGrupo = usuarioStore?.usuario?.grupoNome ?? '';
    if(usuarioGrupo == "Administradores"){
        return "Sim";
    }

    //==================================================================//
    // Normaliza Aprovadores
    // Normaliza Colaborador Logado
    //==================================================================//
    let colaboradorID = colaboradorStore?.colaborador?.id ?? '';
    let aprovadores = dado.aprovadores ?? [];

    //==================================================================//
    // Se o Status da Aprovação for "Aguardando Requisitante"
    // E o Colaborador é um Aprovador dos Aprovadores Esperados. (ACESSO DE APROVACAO: SIM)
    //==================================================================//
    if(dadoStatus == "Aguardando Requisitante"){

        return aprovadores.some((aprovador) => {

            return aprovador?.aprovadorTipo === 'Requisitante' && aprovador?.aprovador?.id === colaboradorID;

        }) ? "Sim" : "Não";

    }

    //==================================================================//
    // Se o Status da Aprovação for "Aguardando Requisitante"
    // E o Colaborador é um Aprovador dos Aprovadores Esperados. (ACESSO DE APROVACAO: SIM)
    //==================================================================//
    if(dadoStatus == "Aguardando Coordenador"){

        return aprovadores.some((aprovador) => {

            return aprovador?.aprovadorTipo === 'Coordenador' && aprovador?.aprovador?.id === colaboradorID;

        }) ? "Sim" : "Não";
        
    }

    //==================================================================//
    // Se o Status da Aprovação for "Aguardando Requisitante"
    // E o Colaborador é um Aprovador dos Aprovadores Esperados. (ACESSO DE APROVACAO: SIM)
    //==================================================================//
    if(dadoStatus == "Aguardando Gestor"){

        return aprovadores.some((aprovador) => {

            return aprovador?.aprovadorTipo === 'Gestor' && aprovador?.aprovador?.id === colaboradorID;

        }) ? "Sim" : "Não";
        
    }

    //==================================================================//
    // Se o Status da Aprovação for "Aguardando Financeiro"
    // E o Colaborador é um Aprovador dos Aprovadores Esperados. (ACESSO DE APROVACAO: SIM)
    //==================================================================//
    if(dadoStatus == "Aguardando Financeiro"){

        return aprovadores.some((aprovador) => {

            return aprovador?.aprovadorTipo === 'Financeiro' && aprovador?.aprovador?.id === colaboradorID;

        }) ? "Sim" : "Não";
        
    }

    //==================================================================//
    // Em qualquer outro caso (ACESSO DE APROVACAO: NÃO)
    //==================================================================//

    return "Não";

};

/*==================================================================*/
/*================== PREPARA APROVADORES ===========================*/
/*==================================================================*/

const preparaAprovadores = (aprovadores = []) => {

    return aprovadores.map((aprovador = {}) => ({
        id: aprovador.id ?? '',
        codigo: aprovador.codigo ?? '',
        status: aprovador.status ?? 'Valido',
        aprovadorTipo: aprovador.aprovadorTipo ?? 'Nenhum',
        aprovador: aprovador.aprovador ?? {},
        decisao: aprovador.decisao ?? 'Nula',
        justificativa: aprovador.justificativa ?? '',
        dataDecisao: aprovador.dataDecisao ?? '',
    }));

};

/*==================================================================*/
/*================== PREPARA DESPESAS ==============================*/
/*==================================================================*/

const preparaDespesas = (despesas = []) => {

    return despesas.map((despesa = {}) => ({
        id: despesa.id ?? '',
        numero: despesa.numero || '',
        despesa: despesa.despesa ?? {},
        dataDaDespesa: despesa.dataDaDespesa ?? '',
        dataCriacao: despesa.dataCriacao ?? '',
        valor: despesa.valor ?? 0,
        veiculoTipo: despesa.veiculoTipo ?? '',
        veiculoPlaca: despesa.veiculoPlaca ?? '',
        veiculoKMInicial: despesa.veiculoKMInicial ?? 0,
        veiculoKMFinal: despesa.veiculoKMFinal ?? 0,
        observacao: despesa.observacao ?? '',
        anexos: despesa.anexos ?? [],
    }));

};

/*==================================================================*/
/*================== PREPARA PARCELAS ==============================*/
/*==================================================================*/

const preparaParcelas = (parcelas = []) => {

    return parcelas.map((parcela = {}) => ({
        id: parcela.id ?? '',
        numero: parcela.numero || '',
        descricao: parcela.descricao ?? '',
        dataCriacao: parcela.dataCriacao ?? '',
        dataDoPagamento: parcela.dataDoPagamento ?? '',
        valor: parcela.valor ?? 0,
    }));

};


/*==================================================================*/
/*================== PREPARA DADOS =================================*/
/*==================================================================*/

const preparaDados = (dadosParaPreparar = []) => {
    
    dados.value = dadosParaPreparar.map((dado = {}) => {

        let despesas = preparaDespesas(dado.despesas ?? []);
        let parcelas = preparaParcelas(dado.parcelas ?? []);
        let aprovadores = preparaAprovadores(dado.aprovadores ?? []);

        dado = {
            id: dado.id ?? '',
            numero: dado.numero ?? '',
            status: dado.status ?? '',
            emitente: dado.emitente ?? '',
            requisitante: dado.requisitante ?? '',
            tipo: dado.tipo ?? '',
            notaFiscal: dado.notaFiscal ?? '',
            tipoCredor: dado.tipoCredor ?? '',
            credorColaborador: dado.credorColaborador ?? '',
            credorFornecedor: dado.credorFornecedor ?? '',
            formaDePagamento: dado.formaDePagamento ?? '',
            recorrencia: dado.recorrencia ?? false,
            recorrenciaQuantidadeTotal: dado.recorrenciaQuantidadeTotal ?? 0,
            recorrenciaQuantidadeAtual: dado.recorrenciaQuantidadeAtual ?? 0,
            dataCriacao: dado.dataCriacao ?? '',
            dataPrevisaoPagamento: dado.dataPrevisaoPagamento ?? '',
            dataAprovacaoPagamento: dado.dataAprovacaoPagamento ?? '',
            valorTotal: dado.valorTotal ?? 0,
            justificativa: dado.justificativa ?? '',
            job: dado.job ?? {},
            ordemDeServico: dado.ordemDeServico ?? {},
            despesas: despesas ?? [],
            parcelas: parcelas ?? [],
            aprovadores: aprovadores ?? [],
            owners: dado.owners ?? [],
        };

        dado.acessoDeEdicao = preparaAcessoDeEdicao(dado ?? {});
        dado.acessoDeAprovacao = preparaAcessoDeAprovacao(dado ?? {});

        return dado;
        
    });
};

/*==================================================================*/
/*========== PROVIDE ALTERA CONTROLES DESATIVADOS ==================*/
/*==================================================================*/

provide('alteraDisable', alteraDisable);

/*==================================================================*/
/*================== ON MOUNTED ====================================*/
/*==================================================================*/

onMounted(async () => {

    await loadOrdensDePagamento();
    preparaDados(ordensDePagamento.value);

});

</script>

<template>

    <TelaTabela
        :disabled="disable"
        :filtros="filtros"
        :headers="dadosHeaders"
        :dados="dadosFiltrados"
    >

        <template #tela-titulo>

            Ordem de Pagamento

        </template>

        <template #toolbar-baixo-direita>

            <BotaoTextoBase
                class="c-botao-criar-dado"
                :disabled="disable"
                :click="aoCriarDado"
            >
                Nova Ordem de Pagamento
                
            </BotaoTextoBase>

        </template>

        <template #tabela-linha-item.credor="{ dado }">

            <template v-if="dado?.tipoCredor === 'Fornecedor'">
                <strong>Fornecedor:</strong> {{ dado?.credorFornecedor?.nomeFantasia || '-' }}
            </template>

            <template v-else-if="dado?.tipoCredor === 'Colaborador'">
                <strong>Colaborador:</strong> {{ dado?.credorColaborador?.nome || '-' }}
            </template>

            <template v-else>
                -
            </template>

        </template>

        <template #tabela-linha-botoes="{ dado, disabled }">
            
            <BotaoIconeBase
                icon="mdi-eye"
                color="#32434d"
                text="Visualizar"
                :disabled="disabled"
                :click="() => aoVisualizarDado(dado)"
            ></BotaoIconeBase>

            <BotaoIconeBase
                v-if="mostraBotaoEditar(dado)"
                icon="mdi-pen"
                color="#3E737B"
                text="Editar"
                :disabled="disabled"
                :click="() => aoEditarDado(dado)"
            ></BotaoIconeBase>

            <BotaoIconeBase
                v-if="mostraBotaoAprovar(dado)"
                icon="mdi-check"
                color="#4F6563"
                text="Aprovar"
                :disabled="disabled"
                :click="() => aoAprovarDado(dado)"
            ></BotaoIconeBase>

        </template>

        <template #modais>

            <CriarDadoModal
                ref="modalCriarDadoRef"
                :disabled="disable"
                :ordemDePagamentoRequisitantes="ordemDePagamentoRequisitantes"
                :ordemDePagamentoCredorColaboradores="ordemDePagamentoCredorColaboradores"
                :ordemDePagamentoCredorFornecedores="ordemDePagamentoCredorFornecedores"
                :ordemDePagamentoDespesas="ordemDePagamentoDespesas"
                :ordemDePagamentoJobs="ordemDePagamentoJobs"
                :onClose="reloadOrdensDePagamento"
            ></CriarDadoModal>

            <VisualisarDadoModal
                ref="modalVisualizarDadoRef"
                :onClose="reloadOrdensDePagamento"
            ></VisualisarDadoModal>

            <EditarDadoModal
                ref="modalEditarDadoRef"
                :disabled="disable"
                :ordemDePagamentoRequisitantes="ordemDePagamentoRequisitantes"
                :ordemDePagamentoCredorColaboradores="ordemDePagamentoCredorColaboradores"
                :ordemDePagamentoCredorFornecedores="ordemDePagamentoCredorFornecedores"
                :ordemDePagamentoDespesas="ordemDePagamentoDespesas"
                :ordemDePagamentoJobs="ordemDePagamentoJobs"
                :onClose="reloadOrdensDePagamento"
            ></EditarDadoModal>

            <AprovarDadoModal
                ref="modalAprovarDadoRef"
                :disabled="disable"
                :ordemDePagamentoRequisitantes="ordemDePagamentoRequisitantes"
                :ordemDePagamentoCredorColaboradores="ordemDePagamentoCredorColaboradores"
                :ordemDePagamentoCredorFornecedores="ordemDePagamentoCredorFornecedores"
                :ordemDePagamentoDespesas="ordemDePagamentoDespesas"
                :ordemDePagamentoJobs="ordemDePagamentoJobs"
                :onClose="reloadOrdensDePagamento"
            ></AprovarDadoModal>

        </template>

    </TelaTabela>

</template>

<style scoped>

.c-botao-criar-dado{
    width: 100%;
}

</style>